import {
  CdkDrag,
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { AccountingCategory, AccountingTransaction } from '../../models/common';
import { FeeChargeRow } from '../fee-charge-config/fee-charge-config.component';
import { UtilityService } from 'src/app/utils/utility.service';
import { NotificationService } from 'src/app/utils/notification.service';
import { MatDialog } from '@angular/material/dialog';
import { CurrencyFormatterService } from '../../services/currency-formatter.service';
import { ProceedModalComponent } from '../../modals/proceed-modal/proceed-modal.component';
import { SelectChargeIdComponent } from '../../modals/select-charge-id/select-charge-id.component';

@Component({
  selector: 'app-drag-drop-list',
  templateUrl: './drag-drop-list.component.html',
  styleUrls: ['./drag-drop-list.component.css'],
})
export class DragDropListComponent implements OnInit {
  @Input() product: 'loan' | 'deposit' = 'loan';
  @Input() isDisabled: boolean = false;
  @Input() createDefaultTxn: boolean = false;
  @Input() accountingCategories: { key: string; value: AccountingCategory }[] =
    [];
  @Input() oldAccountingCategories: {
    key: string;
    value: AccountingCategory;
  }[] = [];
  @Input() glAccounts: any[];
  @Input() charges: any;
  @Input() penaltyCharges: any;
  private previousSelectedId: number | null = null;
  filteredGlAccounts: any[];
  selectedChargeIds: number[] = [];
  filteredEvents: any[];
  filteredEventMapping: any[];
  @Input() events: { label: string; value: string }[] = [];
  @Input() eventMaps: any[] = [];
  mappingNames: any[] = [];
  selectedMappingName: any;
  mappings: any[] = [];

  @Input() feeChargeRows: FeeChargeRow[] = [];
  @Input() accountType: 'ACCRUAL' | 'CASH' = 'ACCRUAL';
  /** In a comparison, the account type before the edit ("Previous: Accrual Based"); empty otherwise. */
  @Input() previousAccountType: string = '';
  /**
   * Keys of categories that must have at least one row before submit is allowed.
   * Passed by the parent so this component stays product-agnostic.
   * Loan:    ['disbursement', 'repayment', 'interest']
   * Deposit: ['deposit', 'withdrawal', 'interest']
   */
  @Input() requiredCategoryKeys: string[] = [];
  @Output() switchAccountTypeEmit: EventEmitter<any> = new EventEmitter();
  @Output() emitAccountingList: EventEmitter<any> = new EventEmitter();

  newTransactionNames: { [key: string]: string } = {};
  isEmptyTransactionName: { [key: string]: boolean } = {};

  public defaultDebitEvents = [
    'REPAYMENT-PRINCIPAL',
    'DISBURSEMENT-INTEREST_PAID',
    'REPAYMENT-INTEREST',
    'DISBURSEMENT-CHARGE_PAID',
  ];

  public defaultCreditEvents = [
    'DISBURSEMENT-PRINCIPAL'
  ]
  private disbursementPrincipalDefault = {
    label: $localize`:@@disbursment_principal:Disbursement Principal`,
    creditGlId: 0,
    // creditGlName: 'FUNDSOURCE-SELECTED',
    eventName: 'DISBURSEMENT-PRINCIPAL',
    // creditGlAccountList: [{name: 'FUNDSOURCE-SELECTED', id: 0}]
  };
  private repaymentPrincipalDefault = {
    label: $localize`:@@repayment_principal:Repayment Principal`,
    debitGlId: 0,
    // debitGlName: 'BRANCH-GL-SELECTED',
    eventName: 'REPAYMENT-PRINCIPAL',
    // debitGlAccountList: [{name: 'BRANCH-GL-SELECTED', id: 0}]
  };
  getLocalizedAddnewTransaction() {
    return $localize`:@@add_new_transaction:Add New Transaction`;
  }
  getLocalizedAddnewTransactionTooltip() {
    return $localize`:@@add_new_transaction:Add New Transaction`;
  }
  private interestDefault = [
    {
      label: $localize`:@@repayment_interest:Repayment Interest`,
      debitGlId: 0,
      // debitGlName: 'FUNDSOURCE-SELECTED',
      eventName: 'REPAYMENT-INTEREST',
      // debitGlAccountList: [{name: 'FUNDSOURCE-SELECTED', id: 0}]
    },
    {
      label: $localize`:@@disbursement_interest_paid:Disbursement Interest Paid`,
      debitGlId: 0,
      // debitGlName: 'FUNDSOURCE-SELECTED',
      eventName: 'DISBURSEMENT-INTEREST_PAID',
      // debitGlAccountList: [{name: 'FUNDSOURCE-SELECTED', id: 0}]
    },
  ];

  constructor(
    private utils: UtilityService,
    private toast: NotificationService,
    private dialog: MatDialog,
    private currencyFormatter: CurrencyFormatterService
  ) {}

  getFeeValueDisplay(row: AccountingTransaction): string {
    const fc = this.getFeeChargeRow(row.chargeId);
    if (!fc?.value || !fc?.chargeType) return '';
    if (fc.chargeType === 'FLAT') {
      const formatted = this.currencyFormatter.formatCurrency(fc.value);
      if (formatted === '-') {
        const num = this.currencyFormatter.parseCurrency(fc.value);
        return num === 0 ? `${this.currencyFormatter.getCurrencySymbol()}0` : fc.value;
      }
      return formatted;
    }
    return `${fc.value}%`;
  }

  ngOnInit(): void {
    this.filteredGlAccounts = this.glAccounts;
    // this.mappingNames = this.eventMaps.map(item => item.mapping_name);
    this.mappingNames = this.eventMaps.map((item) => item.mappings);
    this.filteredEvents = this.mappingNames;

    this.addDefaultTransactions(); //add default transactions to the list
    this.setAllDefaultEvents();
  }

  private setAllDefaultEvents() {
    for (const category of this.accountingCategories) {
      for (const row of category.value.row) {
        if (this.defaultDebitEvents.includes(row.eventName)) {
          row.debitGlId = 0;
        }
        if (this.defaultCreditEvents.includes(row.eventName)) {
          row.creditGlId = 0;
        }
      }
    }
  }

  drop(event: CdkDragDrop<any[]>) {
    const dropListId = event.container.id;
    const index = this.extractIndexFromDropListId(dropListId);

    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
      if (index !== null) {
        event.container.data.forEach((item) => {
          item.groupLabel = this.accountingCategories[index].value.name;
        });
      } else {
      }
    }
  }

  extractIndexFromDropListId(dropListId: string): number | null {
    const match = dropListId.match(/\d+/);

    if (match) {
      return parseInt(match[0], 10);
    } else {
      return null;
    }
  }

  /** Predicate function that does not allow accounting transactions to be dropped into a fees/charges list. */
  dropPredicate() {
    return true;
  }

  noDropPredicate() {
    return false;
  }

  addDefaultTransactions() {
    // Default transactions are loan-specific. Deposit products seed their
    // rows from the API — adding loan defaults would crash because the
    // 'disbursement' and 'repayment' categories don't exist on deposit.
    if (this.product !== 'loan') return;

    const disbursementKey = 'disbursement';
    const repaymentKey = 'repayment';
    const interestKey = 'interest';

    const disbursementCategory = this.accountingCategories.find(
      (cat) => cat.key === disbursementKey
    );
    const repaymentCategory = this.accountingCategories.find(
      (cat) => cat.key === repaymentKey
    );
    const interestCategory = this.accountingCategories.find(
      (cat) => cat.key === interestKey
    );

    if (disbursementCategory.value.row.length === 0)
      this.addTransaction('disbursement', true);
    if (repaymentCategory.value.row.length === 0)
      this.addTransaction('repayment', true);
    if (interestCategory.value.row.length === 0) {
      this.addTransaction('interest', true);
      this.addTransaction('disbursementInterestPaid', true);
    }
  }

  filterCreditGlOptions(searchText: string, row?: AccountingTransaction) {
    row.creditGlAccountList = this.glAccounts;
    if (!searchText) {
      return row.creditGlAccountList;
    }

    return (row.creditGlAccountList = row.creditGlAccountList.filter(
      (glAccount) =>
        glAccount.name.toLowerCase().includes(searchText.toLowerCase())
    ));
  }

  filterDebitGlOptions(searchText: string, row?: AccountingTransaction) {
    row.debitGlAccountList = this.glAccounts;
    if (!searchText) {
      return row.debitGlAccountList;
    }

    return (row.debitGlAccountList = row.debitGlAccountList.filter(
      (glAccount) =>
        glAccount.name.toLowerCase().includes(searchText.toLowerCase())
    ));
  }

  filterEventsOptions(searchText: string, row?: AccountingTransaction) {
    row.eventNameList = [...new Set(this.eventMaps?.flatMap(item => item.mappings.split(', ')) ?? [])];
    if (!searchText) {
      return row.eventNameList;
    }

    return (row.eventNameList = row.eventNameList.filter((event) =>
      event
        .toLowerCase()
        .includes(this.utils.joinWithUnderscore(searchText.toLowerCase()))
    ));
  }

  filterMappingOptions(searchText: string, row?: AccountingTransaction) {
    this.onMappingNameChange(row, row.eventName); // call the mapping change event to preload row.eventMappingList with complete list

    if (!searchText) {
      return row.eventMappingList; //if no search text, return all the eventMapping list
    }

    //else filter eventMapping by search text
    return (row.eventMappingList = row.eventMappingList.filter((eventMap) =>
      eventMap
        .toLowerCase()
        .includes(this.utils.joinWithUnderscore(searchText.toLowerCase()))
    ));
  }

  onMappingNameChange(
    row: AccountingTransaction,
    selectedMappingName: string
  ): void {
    const selectedMapping = row.eventNameList.find(
      (mapping) => mapping === selectedMappingName
    );
    row.eventMapping = selectedMapping.split('-')[1];
    row.eventName = selectedMapping;
    if (this.eventWithDefaultDebit(row)) row.debitGlId = 0;
    if (this.eventWithDefaultCredit(row)) row.creditGlId = 0;
    // row.eventName = selectedMapping.split('-')[0]
  }

  switchAccountType(accountType: 'ACCRUAL' | 'CASH') {
    this.switchAccountTypeEmit.emit(accountType);
  }

  getButtonClass(accountType: 'ACCRUAL' | 'CASH'): string {
    return this.accountType === accountType
      ? 'px-[15px] py-[10px] rounded-[10px] border-[#4d5d76] bg-primary text-white w-[132px] h-[45px]'
      : 'px-[15px] py-[10px] rounded-[10px] border-[#4d5d76] text-primary w-[132px] h-[45px]';
  }

  /* Set once a save or an added row finds a gap. From then on every empty
     field is outlined in red and says so, live, until it is filled - rather
     than a toast naming only the category, which left the reviewer hunting
     for the field. */
  public showErrors = false;

  public fieldMissing(
    row: AccountingTransaction,
    field: 'label' | 'debitGlId' | 'creditGlId' | 'eventName',
  ): boolean {
    if (!this.showErrors) return false;
    const value = row[field];
    return field === 'label' || field === 'eventName'
      ? !String(value ?? '').trim()
      : value === null || value === undefined;
  }

  isTransactionValid(transaction: AccountingTransaction): boolean {
    // A name of spaces is no name.
    const hasLabel = !!String(transaction.label ?? '').trim();
    const hasDebitGlId = transaction.debitGlId != null;
    const hasCreditGlId = transaction.creditGlId != null;
    const hasEventName = !!transaction.eventName;

    const isValid = hasLabel && hasDebitGlId && hasCreditGlId && hasEventName;

    return isValid;

    // return (
    //   !!transaction.label &&
    //   transaction.creditGlId != null &&
    //   transaction.debitGlId != null &&
    //   !!transaction.eventName
    //   // && !!transaction.eventMapping
    // );
  }

  isCategoryValid(category: AccountingCategory): boolean {
    return category.row.every((row) => this.isTransactionValid(row));
  }

  addTransaction(catKey: string, isDefault: boolean = false): void {
    const feesAndChargesKey = 'fees_charges';
    const disbursementKey = 'disbursement';
    const repaymentKey = 'repayment';
    const interestKey = 'interest';
    const disbursementInterestPaidKey = 'disbursementInterestPaid';

    const category = this.accountingCategories.find(
      (cat) => cat.key === catKey
    );

    let newTxnInfo = {
      catKey,
      category,
      newChargeId: null,
      label: '',
      eventName: '',
      creditGlId: null,
      creditGlName: '',
      creditGlAccountList: [],
      debitGlId: null,
      debitGlName: '',
      debitGlAccountList: [],
      siblingCharge: false,
    };

    if (category) {
      if (!this.isCategoryValid(category.value)) {
        this.showErrors = true;
        this.toast.error(
          $localize`:@@complete_all_required_fields:Complete all required fields`
        );
        return;
      }

      if (catKey === feesAndChargesKey) {
        const selectedCharges: { id: number; name: string }[] =
          category.value.row
            .filter(
              (sourceObj) =>
                sourceObj.latePaymentChargeId === null &&
                !sourceObj.siblingCharge
            )
            .map((sourceObj) => ({
              name: sourceObj.label,
              id: sourceObj.chargeId,
            }));

        const dialog = this.dialog.open(SelectChargeIdComponent, {
          disableClose: true,
          data: {
            charges: selectedCharges,
          },
        });
        dialog.afterClosed().subscribe((res) => {
          if (!res.chargeIds) return;
          newTxnInfo.newChargeId = res.chargeIds;
          newTxnInfo.siblingCharge = true;
          const charge = this.charges.find(
            (charge) => charge.id === newTxnInfo.newChargeId
          );
          newTxnInfo.label = charge.name;
          this.createTransactionRow(newTxnInfo);
        });
        if (newTxnInfo.newChargeId === null) return;
      }

      if (catKey === disbursementKey && isDefault) {
        newTxnInfo = { ...newTxnInfo, ...this.disbursementPrincipalDefault };
      }

      if (catKey === repaymentKey && isDefault) {
        newTxnInfo = { ...newTxnInfo, ...this.repaymentPrincipalDefault };
      }

      if (catKey === interestKey && isDefault) {
        this.interestDefault.map((def) => {
          newTxnInfo = { ...newTxnInfo, ...def };
          this.createTransactionRow(newTxnInfo);
        });
        return;
      }

      this.createTransactionRow(newTxnInfo);
    }
  }

  createTransactionRow(newTxnInfo) {
    newTxnInfo.category.value.row.push({
      label: newTxnInfo.label,
      eventName: newTxnInfo.eventName,
      eventNameUnformatted: '',
      debitGlId: newTxnInfo.debitGlId,
      debitGlName: newTxnInfo.debitGlName,
      creditGlName: newTxnInfo.creditGlName,
      creditGlId: newTxnInfo.creditGlId,
      eventMapping: '',
      chargeId: newTxnInfo.newChargeId,
      latePaymentChargeId: null,
      siblingCharge: newTxnInfo.siblingCharge,
      groupLabel: newTxnInfo.category.value.name,
      creditGlAccountList: newTxnInfo.creditGlAccountList,
      debitGlAccountList: newTxnInfo.debitGlAccountList,
      eventMappingList: [],
      eventNameList: this.eventMaps[0]?.mappings?.split(', ') ?? [],
    });

    this.newTransactionNames[newTxnInfo.catKey] = '';
  }

  removeTransaction(catKey: string, index: number): void {
    const dialog = this.dialog.open(ProceedModalComponent, {
      disableClose: true,
      data: {
        message: $localize`:@@confirm_delete:Confirm delete? Action cannot be undone`,
      },
    });
    dialog.afterClosed().subscribe((res) => {
      if (!res) return;
      const category = this.accountingCategories.find(
        (cat) => cat.key === catKey
      );

      if (category) {
        category.value.row.splice(index, 1);
      }
    });
  }

  onInputTransactionName(row: AccountingTransaction, value: string) {
    row.label = this.utils.titleCase(value);
  }

  updateDebitAndCreditGl(
    type: 'debit' | 'credit',
    row: any,
    selectedId: number
  ) {
    const selectedAccount = this.glAccounts.find(
      (glAccount) => glAccount.id === selectedId
    );
    if (selectedAccount) {
      if (type === 'debit') {
        row.debitGlId = selectedAccount.id;
        row.debitGlName = selectedAccount.name;
      } else {
        row.creditGlId = selectedAccount.id;
        row.creditGlName = selectedAccount.name;
      }
    } else {
      if (type === 'debit') {
        row.debitGlId = null;
        row.debitGlName = null;
      } else {
        row.creditGlId = null;
        row.creditGlName = null;
      }
    }
  }


  /**
   * Checks that every required category has at least one row.
   * Required keys are supplied by the parent via [requiredCategoryKeys] so
   * this component stays product-agnostic and doesn't need touching when
   * new product types are added.
   */
  private validateCategories(): boolean {
    for (const key of this.requiredCategoryKeys) {
      const category = this.accountingCategories.find(cat => cat.key === key);
      if (!category || category.value.row.length === 0) {
        this.toast.error(
          `${category?.value.name ?? key} ` +
          $localize`:@@category_requires_transaction:category requires at least one transaction.`
        );
        return false;
      }
    }
    return true;
  }

  mergeRows(): AccountingTransaction[] | undefined {
    // Commented out — let the BE enforce which categories are required.
    // if (!this.validateCategories()) return undefined;

    const mergedRowArray: AccountingTransaction[] = [];

    // 2. Single pass: validate each row then transform it.
    //    Returning undefined stops the parent from emitting to the API.
    for (const category of this.accountingCategories) {
      for (const row of category.value.row) {
        if (!this.isTransactionValid(row)) {
          this.showErrors = true;
          this.toast.error(
            `${category.value.name} ` +
            $localize`:@@category_has_incomplete_transactions:category has incomplete transactions.`
          );
          return undefined;
        }

        const modifiedRow = {
          ...row,
          eventMappingList: undefined,
          creditGlAccountList: undefined,
          debitGlAccountList: undefined,
          eventNameList: undefined,
        };

        if (modifiedRow.eventName.includes('-')) {
          const [eventName, eventMapping] = modifiedRow.eventName.split('-');
          modifiedRow.eventName = eventName.trim();
          modifiedRow.eventMapping = eventMapping.trim();
        }

        modifiedRow.eventName = this.utils.joinWithUnderscore(modifiedRow.eventName);
        modifiedRow.eventMapping = this.utils.joinWithUnderscore(modifiedRow.eventMapping);

        mergedRowArray.push(modifiedRow);
      }
    }

    return mergedRowArray.map(record => {
      record.eventName = record.eventName.toUpperCase();
      record.eventMapping = record.eventMapping.toUpperCase();
      return record;
    });
  }

  submit() {
    const mergedRowArray = this.mergeRows();
    // Only emit when validation passed — undefined means an error toast was
    // already shown and the parent should not proceed to the API.
    if (mergedRowArray !== undefined) {
      this.emitAccountingList.emit(mergedRowArray);
    }
  }

  chargeIdChange(selectedIds: number[]) {
    const categoryKey = 'fees_charges';

    const category = this.accountingCategories.find(
      (cat) => cat.key === categoryKey
    );

    if (category) {
      // Identify the charge IDs that were unselected
      const removedChargeIds = this.selectedChargeIds.filter(
        (id) => !selectedIds.includes(id)
      );

      // Remove rows associated with the unselected charge IDs
      removedChargeIds.forEach((removedChargeId) => {
        // Find all rows with the same chargeId and remove them
        const indexesToRemove = category.value.row
          .map((row, index) => (row.chargeId === removedChargeId ? index : -1))
          .filter((index) => index !== -1);

        // Remove the rows with the specified indexes
        indexesToRemove.reverse().forEach((index) => {
          category.value.row.splice(index, 1);
        });
      });

      // Add rows for newly selected charge IDs
      selectedIds.forEach((chargeId) => {
        // Check if a row with the same chargeId already exists
        const existingRow = category.value.row.find((row) => {
          return row.chargeId === chargeId;
        });

        // If no row with the same chargeId exists, create a new row
        if (!existingRow) {
          const charge = this.charges.find((charge) => charge.id === chargeId);
          if (charge) {
            category.value.row.push(
              {
                label: charge.name,
                eventName: 'DISBURSEMENT-CHARGE_APPLIED',
                debitGlId: null,
                debitGlName: '',
                creditGlName: '',
                creditGlId: null,
                eventMapping: '',
                groupLabel: category.value.name,
                creditGlAccountList: [],
                debitGlAccountList: [],
                eventMappingList: [],
                eventNameList: this.eventMaps[0]?.mappings?.split(', ') ?? [],
                chargeId: chargeId,
                latePaymentChargeId: null,
                siblingCharge: false,
              },
              {
                label: charge.name,
                eventName: 'DISBURSEMENT-CHARGE_PAID',
                debitGlId: 0,
                debitGlName: 'BRANCH-GL-SELECTED',
                creditGlName: '',
                creditGlId: null,
                eventMapping: '',
                groupLabel: category.value.name,
                creditGlAccountList: [],
                debitGlAccountList: [],
                eventMappingList: [],
                eventNameList: this.eventMaps[0].mappings.split(', ') ?? [],
                chargeId: chargeId,
                latePaymentChargeId: null,
                siblingCharge: false,
              }
            );
          }
        }
      });

      this.selectedChargeIds = selectedIds;
    }
  }

  readonly feeEventTypes = [
    { label: 'Daily', value: 'DAILY' }, { label: 'Weekly', value: 'WEEKLY' },
    { label: 'Monthly', value: 'MONTHLY' }, { label: 'Quarterly', value: 'QUARTERLY' },
    { label: 'Semi-Annually', value: 'SEMI_ANNUALLY' }, { label: 'Annually', value: 'ANNUALLY' },
    { label: 'SMS Notification', value: 'SMS_NOTIFICATION' }, { label: 'Email Notification', value: 'EMAIL_NOTIFICATION' },
    { label: 'Withdrawal', value: 'WITHDRAWAL' }, { label: 'Deposit (Third-Party)', value: 'DEPOSIT_THIRD_PARTY' },
    { label: 'Deposit (Own Account)', value: 'DEPOSIT_OWN_ACCOUNT' }, { label: 'Transfer (Third-Party)', value: 'TRANSFER_THIRD_PARTY' },
    { label: 'Transfer (Own Account)', value: 'TRANSFER_OWN_ACCOUNT' }, { label: 'Debit Transaction (Third-Party)', value: 'DEBIT_THIRD_PARTY' },
    { label: 'Debit Transaction (Own Account)', value: 'DEBIT_OWN_ACCOUNT' }, { label: 'Debit Transaction (All)', value: 'DEBIT_ALL' },
    { label: 'Credit Transaction (Third-Party)', value: 'CREDIT_THIRD_PARTY' }, { label: 'Credit Transaction (Own Account)', value: 'CREDIT_OWN_ACCOUNT' },
    { label: 'Credit Transaction (All)', value: 'CREDIT_ALL' }, { label: 'Overdraft Posting', value: 'OVERDRAFT_POSTING' },
    { label: 'Interest Earned', value: 'INTEREST_EARNED' },
  ];
  readonly feeWeekDays = [
    { label: 'Monday', value: 'MONDAY' }, { label: 'Tuesday', value: 'TUESDAY' },
    { label: 'Wednesday', value: 'WEDNESDAY' }, { label: 'Thursday', value: 'THURSDAY' },
    { label: 'Friday', value: 'FRIDAY' }, { label: 'Saturday', value: 'SATURDAY' }, { label: 'Sunday', value: 'SUNDAY' },
  ];
  readonly feeMonthlyOptions = [
    { label: 'First Day of the Month', value: 'FIRST_DAY' },
    { label: 'Last Day of the Month', value: 'LAST_DAY' },
    { label: 'Custom', value: 'CUSTOM' },
  ];
  readonly feeMonths = [
    { label: 'January', value: 'JANUARY' }, { label: 'February', value: 'FEBRUARY' },
    { label: 'March', value: 'MARCH' }, { label: 'April', value: 'APRIL' },
    { label: 'May', value: 'MAY' }, { label: 'June', value: 'JUNE' },
    { label: 'July', value: 'JULY' }, { label: 'August', value: 'AUGUST' },
    { label: 'September', value: 'SEPTEMBER' }, { label: 'October', value: 'OCTOBER' },
    { label: 'November', value: 'NOVEMBER' }, { label: 'December', value: 'DECEMBER' },
  ];
  readonly feeTimeOptions = [
    '12:00AM','01:00AM','02:00AM','03:00AM','04:00AM','05:00AM',
    '06:00AM','07:00AM','08:00AM','09:00AM','10:00AM','11:00AM',
    '12:00PM','01:00PM','02:00PM','03:00PM','04:00PM','05:00PM',
    '06:00PM','07:00PM','08:00PM','09:00PM','10:00PM','11:00PM','11:59PM',
  ];

  getFeeChargeRow(chargeId: number): FeeChargeRow | undefined {
    return this.feeChargeRows.find(r => r.chargeId === chargeId);
  }

  syncFeeChargeRows(configRows: FeeChargeRow[], charges: any[], defaultEventName = 'DISBURSEMENT-CHARGE_APPLIED'): void {
    const category = this.accountingCategories.find(c => c.key === 'fees_charges');
    if (!category) return;

    const previousRows = [...category.value.row];
    const defaultEventNameList = [...new Set(this.eventMaps?.flatMap(item => item.mappings.split(', ')) ?? [])];
    // The late payment charge's row has no fee row behind it, so rebuilding
    // from the fees would drop it; it is carried over as it stands.
    const latePaymentRows = previousRows.filter((r: any) => !!r.latePaymentChargeId);

    const feeRows = configRows
      .filter(r => r.chargeId !== null)
      .map(configRow => {
        const charge = charges?.find((c: any) => c.id === configRow.chargeId);
        const existing = previousRows.find((r: any) => r.chargeId === configRow.chargeId);
        return {
          label: charge?.name ?? '',
          feeValue: configRow.value,
          chargeType: configRow.chargeType || charge?.chargeType || '',
          debitGlId: existing?.debitGlId ?? null,
          debitGlName: existing?.debitGlName ?? '',
          creditGlId: existing?.creditGlId ?? null,
          creditGlName: existing?.creditGlName ?? '',
          eventName: existing?.eventName ?? defaultEventName,
          eventMapping: existing?.eventMapping ?? '',
          groupLabel: category.value.name,
          creditGlAccountList: existing?.creditGlId && existing?.creditGlName
            ? [{ id: existing.creditGlId, name: existing.creditGlName }]
            : (existing?.creditGlAccountList ?? []),
          debitGlAccountList: existing?.debitGlId && existing?.debitGlName
            ? [{ id: existing.debitGlId, name: existing.debitGlName }]
            : (existing?.debitGlAccountList ?? []),
          eventMappingList: existing?.eventMappingList ?? [],
          eventNameList: existing?.eventNameList?.length ? existing.eventNameList : defaultEventNameList,
          chargeId: configRow.chargeId,
          latePaymentChargeId: null,
          siblingCharge: false,
        };
      });

    category.value.row = [...feeRows, ...latePaymentRows];
  }

  latePaymentchargeIdChange(selectedId: number) {
    const categoryKey = 'fees_charges';

    const category = this.accountingCategories.find(
      (cat) => cat.key === categoryKey
    );

    if (category) {
      // Remove the row associated with the previous selected charge ID
      if (this.previousSelectedId !== null) {
        const previousRow = category.value.row.find(
          (row) => row.latePaymentChargeId === this.previousSelectedId
        );
        if (previousRow) {
          const indexToRemove = category.value.row.indexOf(previousRow);
          category.value.row.splice(indexToRemove, 1);
        }
      }

      //if selectedId is null exit
      if (selectedId === null) return;

      const charge = this.penaltyCharges.find(
        (charge) => charge.id === selectedId
      );
      if (charge) {
        const eventName = 'CRON';
        const eventMapping = 'PENALTY_RECEIVABLE';
        const selectedMapping = this.eventMaps.find(
          (item) => item.mapping_name.toLowerCase() === eventName.toLowerCase()
        );
        category.value.row.push({
          label: charge.name,
          eventName: this.utils.titleCase(
            eventName + ' ' + this.utils.underScoreToSpace(eventName)
          ),
          debitGlId: null,
          debitGlName: '',
          creditGlName: '',
          creditGlId: null,
          eventMapping: this.utils.titleCase(
            this.utils.underScoreToSpace(eventMapping)
          ),
          groupLabel: category.value.name,
          creditGlAccountList: [],
          debitGlAccountList: [],
          eventMappingList: selectedMapping.mappings.split(', ') ?? [],
          eventNameList: this.eventMaps[0].mappings.split(', ') ?? [],
          chargeId: null,
          latePaymentChargeId: selectedId,
        });
      }

      this.previousSelectedId = selectedId;
    }
  }

  isLatePaymentCharge(eventName: string, eventMapping: string): boolean {
    if (
      eventName.toLocaleLowerCase() === 'cron' &&
      eventMapping.toLocaleLowerCase() === 'penalty receivable'
    ) {
      return true;
    }

    return false;
  }

  isContainCharge(catKey: string): boolean {
    const categoryKey = 'fees_charges';
    if (catKey !== categoryKey) return true;

    const category = this.accountingCategories.find(
      (cat) => cat.key === catKey
    );

    if (category) {
      return category.value.row.some((row) => row.chargeId !== null);
    }

    // Return false if the category with the specified key is not found
    return false;
  }

  notSiblingCharge(row: AccountingTransaction): boolean {
    //Returns true when row has a charge and not a sibling charge
    return row.chargeId && !row.siblingCharge;
  }

  isDefaultDisbursement(row: AccountingTransaction): boolean {
    return row.eventName === 'DISBURSEMENT-PRINCIPAL';
  }
  isDefaultRepayment(row: AccountingTransaction): boolean {
    return row.eventName === 'REPAYMENT-PRINCIPAL';
  }
  isDefaultInterest(row: AccountingTransaction): boolean {
    return row.eventName === 'REPAYMENT-INTEREST';
  }
  isDefaultFee(row: AccountingTransaction): boolean {
    return row.eventName === 'DISBURSEMENT-CHARGE_PAID';
  }



  eventWithDefaultDebit(row: AccountingTransaction) {

    return this.defaultDebitEvents.includes(row.eventName);
  }
  eventWithDefaultCredit(row: AccountingTransaction) {
    return this.defaultCreditEvents.includes(row.eventName);
  }
}
